import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  useTheme
} from '@mui/material'
import { Warning as WarningIcon } from '@mui/icons-material'

interface DeleteConfirmModalProps {
  isOpen: boolean
  lotName: string
  onConfirm: () => void
  onCancel: () => void
}

export default function DeleteConfirmModal({
  isOpen,
  lotName,
  onConfirm,
  onCancel
}: DeleteConfirmModalProps) {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'

  return (
    <Dialog
      open={isOpen}
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          bgcolor: 'background.paper',
          backgroundImage: 'none',
          border: '1px solid',
          borderColor: 'divider'
        }
      }}
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: isDark ? 'rgba(0, 0, 0, 0.7)' : 'rgba(0, 0, 0, 0.5)'
        }
      }}
    >
      <DialogTitle sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        pb: 2,
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 48,
          height: 48,
          borderRadius: '50%',
          bgcolor: isDark ? 'rgba(239, 68, 68, 0.15)' : 'rgba(239, 68, 68, 0.1)',
          color: 'error.main',
          flexShrink: 0
        }}>
          <WarningIcon />
        </Box>
        <Typography variant="h6" component="div" sx={{
          fontWeight: 600,
          color: 'text.primary'
        }}>
          프로젝트 삭제
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <Typography variant="body1" sx={{
          mb: 2,
          color: 'text.primary',
          '& strong': {
            fontWeight: 600,
            color: 'error.main'
          }
        }}>
          정말로 <strong>{lotName}</strong> 프로젝트를 삭제하시겠습니까?
        </Typography>

        <Alert
          severity="warning"
          sx={{
            mt: 2,
            bgcolor: isDark ? 'rgba(245, 158, 11, 0.15)' : 'rgba(245, 158, 11, 0.1)',
            color: 'warning.main',
            '& .MuiAlert-icon': {
              color: 'warning.main'
            },
            '& .MuiAlert-message': {
              color: 'text.primary'
            }
          }}
        >
          이 작업은 되돌릴 수 없습니다. 이 프로젝트와 관련된 모든 층, 객체, 데이터가 영구적으로 삭제됩니다.
        </Alert>
      </DialogContent>

      <DialogActions sx={{
        px: 3,
        pb: 3,
        pt: 2,
        gap: 1,
        borderTop: '1px solid',
        borderColor: 'divider'
      }}>
        <Button
          onClick={onCancel}
          variant="outlined"
          sx={{
            color: 'text.secondary',
            borderColor: 'divider',
            '&:hover': {
              borderColor: 'text.secondary',
              bgcolor: 'action.hover',
              color: 'text.primary'
            }
          }}
        >
          취소
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          sx={{
            bgcolor: 'error.main',
            color: 'error.contrastText',
            '&:hover': {
              bgcolor: 'error.dark'
            },
            boxShadow: isDark
              ? '0 4px 12px rgba(239, 68, 68, 0.3)'
              : '0 4px 12px rgba(239, 68, 68, 0.2)'
          }}
        >
          프로젝트 삭제
        </Button>
      </DialogActions>
    </Dialog>
  )
}
